import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  Stack,
  Card,
  CardContent,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  CourseLanguageOverrides,
  QuestionMetadata,
  UserQuizSubmission,
  CourseMetadata,
} from "@backend-types";
import { useHistory } from "react-router-dom";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import {
  ComputedQuizScore,
  CourseStructureCtx,
  LockStatus,
  UserCtx,
} from "../../../interfaces";
import { HandleDataLayer } from "../../../Helpers";
import { Question, Target as TargetTree } from "../../utils/CourseTree";
import { TARGET_COMPLETION_STATUS } from "../../../enums";
import { UserContext } from "../../../context/UserContext";
import { SubmoduleProgressIndicator } from "../utils/SubmoduleProgressIndicator";

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    minWidth: "378px",
    minHeight: "104px",
    width: "100%",
    padding: "24px 32px 24px 24px",
    transition: "box-shadow 200ms ease-in-out",
    cursor: "pointer",
  },
  scoreIndicator: {
    marginLeft: "unset",
    width: "150px",
    height: "88px",
    backgroundColor: "#FFF",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 16px",
  },
  statusIndicator: {
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusCompleted: {
    backgroundColor: theme.palette.success.main,
  },
  statusInProgress: {
    backgroundColor: "#FDEDF1",
    color: "#D81D4F",
  },
  statusNeedsPractice: {
    backgroundColor: theme.palette.warning.main,
  },
  statusMastered: {
    backgroundColor: "#E9F5DB",
    color: "#477213",
  },
  statusNotStarted: {
    backgroundColor: "#E8E8E8",
    color: "#484848",
  },
}));

export interface BioSpineLessonCardProps {
  target: TargetTree;
  lockStatus?: LockStatus;
  targetQuizSubmissions: UserQuizSubmission[];
  resumeQuestion: Question | undefined;
  last: boolean;
  openSkillVideoModal: () => void;
  status: TARGET_COMPLETION_STATUS;
  cardVariant?: "student" | "teacher";
  languageOverrides?: CourseLanguageOverrides;
  groupTargets?: boolean;
}

export function BioSpineLessonCard(props: BioSpineLessonCardProps) {
  const {
    target,
    lockStatus, // deprecated AWS TargetGroup feature
    resumeQuestion,
    status,
    cardVariant = "student",
    groupTargets = false,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentTargetName = target.getFormattedTargetName();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const quizScore = useMemo<ComputedQuizScore | null>(() => {
    return userCtx.calculateQuizScore([target]);
  }, [target, userCtx]);

  const learningMapProgress = useMemo<ComputedQuizScore | null>(() => {
    return userCtx.calculateLearningMapProgress([target]);
  }, [target, userCtx]);

  // currently for BioSpine there will only be 1 question in the target but this is just in case that ever changes
  const metadataTotals = useMemo<QuestionMetadata>(() => {
    return target.getQuestionMetadata();
  }, [target]);

  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();

  const handleStart = () => {
    if (!resumeQuestion || lockStatus?.is_locked) return;
    HandleDataLayer(
      "link",
      "click",
      "internal link",
      "main content",
      "lessons",
      `${currentTargetName}`,
      "onclick",
      `${target?.parent?.name ?? ""}`
    );
    if (cardVariant === "student") {
      courseStructureCtx.setBackBehavior({ backTo: "moduleoverview" });
    } else {
      //module=8.EE.C.7.1&courseId=1&content=1
      courseStructureCtx.setBackBehavior({
        backTo: "deeplinking",
        route: "/explore/activities",
        search: new URLSearchParams({
          module: `${target?.module_id}`,
          courseId: `${courseStructureCtx.courseTreeStructure?.getCurrentCourse().id}`,
          content: "1",
        }).toString(),
      });
    }
    history.push({
      pathname: "/solver",
      search: "&questionId=" + resumeQuestion.id,
    });
  };

  const percentPracticed = learningMapProgress?.percentage
    ? Math.round(learningMapProgress?.percentage * 100)
    : 0;

  return (
    <Card
      component="button"
      type="button"
      tabIndex={0}
      className={classes.cardContainer}
      onClick={handleStart}
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
      sx={{
        textAlign: "left",
        ...(groupTargets
          ? {
              border: "none",
              borderRadius: "0px",
              "&:hover": { backgroundColor: "#F3F3F3" },
            }
          : {
              marginBottom: "24px",
              "&:hover": props.lockStatus?.is_locked
                ? { cursor: "unset" }
                : { boxShadow: "0px 8px 12px #00000029" },
            }),
      }}>
      <CardContent
        sx={{
          height: "100%",
          padding: "0px !important",
          display: "flex",
          flexDirection: "column",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            width: "100%",
            alignItems: "stretch",
          }}>
          <Stack
            direction="column"
            sx={{
              flexGrow: 1,
              flexBasis: "0",
              flexShrink: 1,
              overflow: "hidden",
              marginRight: "auto",
            }}>
            <Typography
              fontSize="1.5em"
              lineHeight="36px"
              marginBottom="16px"
              width="100%"
              variant="h2"
              fontWeight={600}
              sx={{
                "&:hover": {
                  textDecoration: target.is_asset_locked ? "unset" : "underline",
                },
              }}>
              {currentTargetName}
            </Typography>

            {courseMetadata?.management_page?.show_submodule_progress && (
              <Stack sx={{ marginBottom: "16px", maxWidth: "592px" }}>
                <SubmoduleProgressIndicator
                  percentage={
                    learningMapProgress?.percentage
                      ? Math.round(learningMapProgress?.percentage * 100)
                      : 0
                  }
                />
              </Stack>
            )}

            <Stack direction="row" alignItems="center" marginTop="auto" spacing={2}>
              <LessonProgressIndicator status={status} />

              {courseMetadata?.management_page?.show_submodule_progress && (
                <Stack direction="row" spacing={2}>
                  {
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: "#5D5D5D",
                      }}
                      variant="body2"
                      component="span">
                      {percentPracticed > 0
                        ? `${Math.round(percentPracticed)}% Practiced`
                        : "Not started"}
                      {/* 80% Practiced */}
                    </Typography>
                  }
                </Stack>
              )}

              {!courseMetadata?.management_page?.assessment_metrics_type ||
              courseMetadata.management_page.assessment_metrics_type ===
                "activity-type-count" ? (
                <Stack direction="row" spacing={2}>
                  {[
                    {
                      count: Number(metadataTotals.primary_videos ?? 0),
                      labelSingular: "lesson video",
                      labelPlural: "lesson videos",
                    },
                    {
                      count: Number(metadataTotals.primary_quizzes ?? 0),
                      labelSingular: "practice activity",
                      labelPlural: "practice activities",
                    },
                    {
                      count: Number(metadataTotals.graded_quizzes ?? 0),
                      labelSingular: "graded quiz",
                      labelPlural: "graded quizzes",
                    },
                    {
                      count: Number(metadataTotals.supplementary_quizzes ?? 0),
                      labelSingular: "additional activity",
                      labelPlural: "additional activities",
                    },
                  ]
                    .filter((item) => item.count > 0)
                    .map((item, index, array) => (
                      <React.Fragment key={index}>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            color: "#5D5D5D",
                          }}
                          variant="body2"
                          component="span">
                          {item.count}{" "}
                          {item.count === 1 ? item.labelSingular : item.labelPlural}
                        </Typography>
                        {/* Add divider if not the last item */}
                        {index < array.length - 1 && (
                          <Divider orientation="vertical" flexItem />
                        )}
                      </React.Fragment>
                    ))}
                </Stack>
              ) : (
                <Stack direction="row" spacing={2}>
                  {
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: "#5D5D5D",
                      }}
                      variant="body2"
                      component="span">
                      {learningMapProgress?.max_points}{" "}
                      {learningMapProgress?.max_points === 1 ? "Question" : "Questions"}
                    </Typography>
                  }
                </Stack>
              )}
            </Stack>
          </Stack>
          <Stack justifyContent="center">
            <ScoreIndicator
              quizScore={quizScore}
              status={status}
              target={target}
              lockStatus={lockStatus}
              tooltipOpen={tooltipOpen}
              courseMetadata={courseMetadata}
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

interface LessonProgressIndicatorProps {
  status: TARGET_COMPLETION_STATUS;
}

interface IProgressIndicator {
  class: string;
  text: string;
}

function LessonProgressIndicator(props: LessonProgressIndicatorProps) {
  const classes = useStyles();

  const getStatus = (): IProgressIndicator => {
    switch (props.status) {
      case TARGET_COMPLETION_STATUS.IN_PROGRESS:
        return { class: classes.statusInProgress, text: "IN PROGRESS" };
      case TARGET_COMPLETION_STATUS.MASTERED:
        return { class: classes.statusMastered, text: "COMPLETED" };
      case TARGET_COMPLETION_STATUS.NEEDS_PRACTICE:
        return { class: classes.statusNeedsPractice, text: "NEEDS PRACTICE" };
      case TARGET_COMPLETION_STATUS.NOT_STARTED:
      default:
        return { class: classes.statusNotStarted, text: "NOT STARTED" };
    }
  };

  const status = getStatus();

  return (
    <Box className={`${classes.statusIndicator} ${status.class}`}>
      <Typography variant="body1" lineHeight="1em">
        {status.text}
      </Typography>
    </Box>
  );
}

interface ScoreIndicatorProps {
  quizScore?: ComputedQuizScore | null;
  status: TARGET_COMPLETION_STATUS;
  target: TargetTree;
  lockStatus?: LockStatus;
  tooltipOpen?: boolean;
  courseMetadata?: CourseMetadata;
}

interface IScoreButtonProps {
  color: string;
  icon: string;
  label: string | JSX.Element;
  tooltip: string | JSX.Element;
  datalayerText: string;
}

function ScoreIndicator(props: ScoreIndicatorProps) {
  const { quizScore, target } = props;
  const classes = useStyles();
  const metadataTotals = useMemo<QuestionMetadata>(() => {
    return target.getQuestionMetadata();
  }, [target]);

  const getButtonProps = (): IScoreButtonProps => {
    if (props.lockStatus && props.lockStatus.is_locked) {
      return {
        color: "#484848",
        icon: "fa-solid fa-lock",
        label: props.lockStatus.label,
        tooltip: props.lockStatus.tooltip,
        datalayerText: "disabled lock button",
      };
    }

    //the string Bio370 is per ticket MACSUAT-5664
    if (
      ((quizScore && quizScore.max_points === 0) ||
        (!quizScore && !metadataTotals.primary_quizzes)) &&
      !target.module_id.includes("Bio.370")
    ) {
      return {
        color: "#0092E0",
        icon: "fa-solid fa-book",
        label: "No Quiz",
        tooltip: "No Quiz",
        datalayerText: "blue book button",
      };
    } else if (
      props.courseMetadata?.module_overview_page?.disable_try_again_icon &&
      props.quizScore?.percentage !== null
    ) {
      return {
        color: "#0092E0",
        icon: "fa-solid fa-file-pen",
        label: "Review",
        tooltip: "Review",
        datalayerText: "blue book button",
      };
    } else if (
      target.module_id.includes("Bio.370") &&
      props.status === TARGET_COMPLETION_STATUS.MASTERED
    ) {
      return {
        color: "#477213",
        icon: "fa-solid fa-trophy",
        label: "Amazing",
        tooltip: "Amazing",
        datalayerText: "green paper",
      };
    } else if (props.quizScore?.percentage === null) {
      return {
        color: "#0092E0",
        icon: "fa-solid fa-play",
        label:
          props.status === TARGET_COMPLETION_STATUS.NOT_STARTED ? "Start" : "Continue",
        tooltip:
          props.status === TARGET_COMPLETION_STATUS.NOT_STARTED ? "Start" : "Continue",
        datalayerText: "blue play button",
      };
    } else if (
      props.quizScore &&
      props.quizScore?.percentage !== null &&
      props.quizScore?.percentage < 0.8
    ) {
      return {
        color: "#F96712",
        icon: "fa-solid fa-circle-exclamation",
        label: "Try again",
        tooltip: "Try again",
        datalayerText: "orange arrow",
      };
    } else {
      return {
        color: "#477213",
        icon: "fa-solid fa-trophy",
        label: "Amazing",
        tooltip: "Amazing",
        datalayerText: "green paper",
      };
    }
  };
  const buttonProps = getButtonProps();

  const scoreDisplay = () => {
    switch (props.quizScore?.invalid) {
      case "No Quiz":
        return "N/A";
      case "-":
        return "-";
      case undefined:
      default:
        return props.quizScore?.display_as === "percentage"
          ? `${Math.round((props.quizScore.percentage ?? 0) * 100)}%`
          : props.quizScore?.display_as === "points"
          ? `${props.quizScore.points ?? 0}/${props.quizScore.max_points ?? 0}`
          : "-";
    }
  };

  return (
    <Box
      className={classes.scoreIndicator}
      sx={{
        "&:hover": {
          border: props.lockStatus?.is_locked
            ? undefined
            : `1px solid ${buttonProps.color}`,
        },
      }}
      onClick={(event) => {
        HandleDataLayer(
          "link",
          "click",
          "internal link",
          "main content",
          `${props.target.getFormattedTargetName()}`,
          `${
            props.quizScore?.percentage !== null
              ? `${Math.round((props.quizScore?.percentage ?? 0) * 100)}%`
              : "-"
          } ^ ${buttonProps.datalayerText}`,
          "onclick"
        );
      }}>
      <Box>
        <div
          tabIndex={0}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "27px",
            height: "25px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
          }}
          aria-label={buttonProps.label + " " + props.target.getFormattedTargetName()}>
          <Tooltip
            placement={props.lockStatus?.is_locked ? "bottom-end" : "bottom"}
            arrow
            title={buttonProps.tooltip}
            open={props.lockStatus?.is_locked ? props.tooltipOpen : undefined}
            PopperProps={
              props.lockStatus?.is_locked
                ? {
                    sx: {
                      // Shift the tooltip box
                      [`&.${tooltipClasses.popper}[data-popper-placement*="bottom-end"] .${tooltipClasses.tooltip}`]:
                        {
                          marginRight: "-8px",
                        },
                      // Shift the arrow back 8px to the left to compensate
                      [`&.${tooltipClasses.popper}[data-popper-placement*="bottom-end"] .${tooltipClasses.arrow}`]:
                        {
                          marginLeft: "-4px",
                        },
                      [`& .MuiTooltip-tooltip`]: {
                        maxWidth: "none",
                      },
                    },
                  }
                : undefined
            }>
            <Typography
              fontWeight={900}
              fontSize="24px"
              lineHeight="24px"
              color={buttonProps.color}>
              <i className={buttonProps.icon}></i>
            </Typography>
          </Tooltip>
        </div>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}>
        <Typography
          variant="h4"
          component="span"
          style={{
            display: "block",
            fontWeight: 600,
            fontSize: "28px",
            lineHeight: "26px",
            margin: 0,
          }}>
          {scoreDisplay()}
        </Typography>
        <Typography
          sx={{
            display: "block",
            fontSize: "16px",
            color: "#191919",
          }}
          variant="body2"
          component="span">
          {buttonProps.label}
        </Typography>
      </Box>
      <Box sx={{ color: "#747474", fontSize: "16px" }}>
        <i className="fa-solid fa-chevron-right"></i>
      </Box>
    </Box>
  );
}
